import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../cn';
import Button from '../Button';
function onlyDigits(value) {
    return value.replace(/\D/g, '');
}
function computePlacement(trigger) {
    return {
        top: trigger.bottom + 8,
        left: trigger.left + trigger.width / 2,
    };
}
export function PageJumpEllipsis({ variant = 'admin', disabled = false, totalPages, onSubmit, className, }) {
    const popoverId = useId();
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const inputRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [pageInput, setPageInput] = useState('');
    const [placement, setPlacement] = useState(null);
    const reset = useCallback(() => setPageInput(''), []);
    const close = useCallback(() => {
        setOpen(false);
        reset();
    }, [reset]);
    const updatePlacement = useCallback(() => {
        const el = triggerRef.current;
        if (!el)
            return;
        setPlacement(computePlacement(el.getBoundingClientRect()));
    }, []);
    useLayoutEffect(() => {
        if (!open) {
            setPlacement(null);
            return;
        }
        updatePlacement();
        window.setTimeout(() => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true }); }, 0);
    }, [open, updatePlacement]);
    useEffect(() => {
        if (!open)
            return;
        const onScrollOrResize = () => updatePlacement();
        window.addEventListener('resize', onScrollOrResize);
        window.addEventListener('scroll', onScrollOrResize, true);
        return () => {
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
        };
    }, [open, updatePlacement]);
    useEffect(() => {
        if (!open)
            return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape')
                close();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, close]);
    useEffect(() => {
        if (!open)
            return;
        const onMouseDown = (event) => {
            var _a, _b;
            const t = event.target;
            if (!t)
                return;
            if ((_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.contains(t))
                return;
            if ((_b = panelRef.current) === null || _b === void 0 ? void 0 : _b.contains(t))
                return;
            close();
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [open, close]);
    const parsedPage = pageInput === '' ? null : Number.parseInt(pageInput, 10);
    const isValid = parsedPage != null && parsedPage >= 1 && parsedPage <= totalPages;
    const handleSubmit = useCallback(() => {
        if (!isValid || parsedPage == null)
            return;
        onSubmit(parsedPage);
        close();
    }, [close, isValid, onSubmit, parsedPage]);
    const handleChange = (e) => {
        const digits = onlyDigits(e.target.value);
        if (digits === '') {
            setPageInput('');
            return;
        }
        const num = Number.parseInt(digits, 10);
        if (num > totalPages) {
            setPageInput(String(totalPages));
            return;
        }
        setPageInput(digits);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        }
    };
    const popoverPanel = open && placement ? (React.createElement("div", { ref: panelRef, id: popoverId, role: "dialog", "aria-label": "Jump to page", className: cn('fixed z-[9999] -translate-x-1/2 rounded-lg border border-border bg-popover px-2.5 py-2 text-popover-foreground shadow-lg', variant === 'portal' && 'border-gray-200 bg-white text-gray-900'), style: { top: placement.top, left: placement.left }, onClick: (e) => e.stopPropagation() },
        React.createElement("div", { className: cn('absolute -top-1.5 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rotate-45 border-l border-t', variant === 'portal'
                ? 'border-gray-200 bg-white'
                : 'border-border bg-popover'), "aria-hidden": true }),
        React.createElement("div", { className: "relative flex items-center gap-1.5" },
            React.createElement("input", { ref: inputRef, type: "text", inputMode: "numeric", value: pageInput, onChange: handleChange, onKeyDown: handleKeyDown, placeholder: "Page", "aria-label": "Page number", className: cn('h-7 w-14 rounded-md border bg-background px-2 text-sm tabular-nums outline-none', 'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1', variant === 'portal'
                    ? 'border-gray-200 text-gray-900 placeholder:text-gray-400'
                    : 'border-input text-foreground placeholder:text-muted-foreground') }),
            React.createElement("span", { className: "text-xs text-muted-foreground tabular-nums" },
                "/ ",
                totalPages),
            variant === 'portal' ? (React.createElement("button", { type: "button", disabled: !isValid, onClick: handleSubmit, className: "h-7 rounded-md bg-orange-500 px-2.5 text-xs font-medium text-white hover:bg-orange-600 disabled:opacity-40" }, "Go")) : (React.createElement(Button, { type: "button", size: "sm", disabled: !isValid, onClick: handleSubmit, className: "h-7 px-2.5 text-xs" }, "Go"))))) : null;
    const toggleOpen = () => {
        if (disabled)
            return;
        setOpen((v) => {
            if (v)
                reset();
            return !v;
        });
    };
    return (React.createElement(React.Fragment, null,
        variant === 'portal' ? (React.createElement("button", { ref: triggerRef, type: "button", disabled: disabled, onClick: toggleOpen, className: className, "aria-label": "Jump to page", "aria-haspopup": "dialog", "aria-expanded": open, "aria-controls": open ? popoverId : undefined }, "\u2026")) : (React.createElement(Button, { ref: triggerRef, type: "button", variant: "outline", size: "sm", disabled: disabled, onClick: toggleOpen, className: className, "aria-label": "Jump to page", "aria-haspopup": "dialog", "aria-expanded": open, "aria-controls": open ? popoverId : undefined }, "\u2026")),
        typeof document !== 'undefined' && popoverPanel ? createPortal(popoverPanel, document.body) : null));
}
export default PageJumpEllipsis;
